import { Checkbox, Spacer } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IPatient } from '@common/interfaces/patient';
import { ProfessionalSummaryDataTable } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryDataTable';
import type { IAllergy } from '../../../../../interfaces/allergy';
import type { Allergies, AllergyRecord } from './allergies';
import { ProfessionalSummaryDataType } from '../../../../../../../common/enums/professionalSummaryDataType';
import { translateKey } from '../../../../../../translate';
import { LinkOnClick, TableBody, TableData, TableHead, TableHeader, TableRow } from '../../../../../elements/common';
import { NullableFormattedDate } from '../../../../../elements/NullableFormattedDate';
import { ProfessionalSummaryDataPlaceholder } from '../../../../professionalSummaryData/ProfessionalSummaryDataPlaceholder';
import {
    ProfessionalSummaryDataAction,
    ProfessionalSummaryDataSection,
} from '../../../../professionalSummaryData/ProfessionalSummaryDataSection';
import {
    filterAndPrepareAllergiesForSaving,
    isCurrentAllergy,
    loadProcessActivityAllergies,
    loadRequestAllergies,
} from './allergies';

interface IAllergiesAddEditProps {
    onChange: (allergies: IAllergy[]) => Promise<void>;
    patient: IPatient;
    processActivityId?: string | null;
    requestId?: string;
}

export function AllergiesAddEdit(props: IAllergiesAddEditProps) {
    const { patient } = props;
    const [allergies, setAllergies] = useState<Allergies>();
    const [allAllergiesSelected, setAllAllergiesSelected] = useState<boolean>(false);
    const [areAllergiesLoaded, setAreAllergiesLoaded] = useState<boolean>(false);

    const [areAllergiesOpen, setAreAllergiesOpen] = useState(false);
    const [selectedAllergiesCount, setSelectedAllergiesCount] = useState<number>(0);
    const [loadFailed, setLoadFailed] = useState<boolean>(false);

    useEffect(() => {
        loadAllergies();
    }, []);

    useEffect(() => {
        setAllAllergiesSelected(areAllAllergiesSelected());
        setSelectedAllergiesCount(getNumberOfAllergiesSelected());
        props.onChange(filterAndPrepareAllergiesForSaving(allergies));
    }, [allergies]);

    function areAllAllergiesSelected() {
        return allergies ? allergies.every((allergy) => allergy?.action === ProfessionalSummaryDataAction.pick) : false;
    }

    function getNumberOfAllergiesSelected() {
        return allergies
            ? allergies.filter((allergy) => allergy?.action === ProfessionalSummaryDataAction.pick).size
            : 0;
    }

    function patientHasAllergies() {
        return areAllergiesLoaded && allergies !== undefined && allergies.size > 0;
    }

    function selectOrDeselectAllAllergies(isSelect: boolean) {
        if (!allergies) {
            return;
        }

        const finalAllergies = allergies.map((allergy) => {
            if (allergy?.action === ProfessionalSummaryDataAction.skip && isSelect) {
                return allergy.set('action', ProfessionalSummaryDataAction.pick);
            } else if (allergy?.action === ProfessionalSummaryDataAction.pick && !isSelect) {
                return allergy.set('action', ProfessionalSummaryDataAction.skip);
            } else {
                return allergy;
            }
        });

        setAllergies(finalAllergies as Allergies);
        setAllAllergiesSelected(isSelect);
    }

    return (
        <ProfessionalSummaryDataSection
            extraHeaderElements={
                patientHasAllergies() ? (
                    <Spacer
                        direction="horizontal"
                        spacing="small"
                    >
                        <LinkOnClick
                            className="mr-2"
                            onClick={() => selectOrDeselectAllAllergies(!allAllergiesSelected)}
                        >
                            {translateKey('professionalSummary.all')}
                        </LinkOnClick>
                        <LinkOnClick
                            className="mr-2"
                            onClick={() => selectActiveAllergies()}
                        >
                            {translateKey('professionalSummary.current')}
                        </LinkOnClick>
                    </Spacer>
                ) : undefined
            }
            isOpen={areAllergiesOpen}
            onToggle={() => setAreAllergiesOpen(!areAllergiesOpen)}
            professionalSummaryDataType={ProfessionalSummaryDataType.allergies}
            selectionCount={selectedAllergiesCount}
        >
            {patientHasAllergies() && (
                <ProfessionalSummaryDataTable>
                    <TableHead>
                        <TableRow>
                            <TableHeader></TableHeader>
                            <TableHeader>{translateKey('allergies.displayName')}</TableHeader>
                            <TableHeader>{translateKey('allergies.description')}</TableHeader>
                            <TableHeader align="right">{translateKey('common.startDate')}</TableHeader>
                            <TableHeader align="right">{translateKey('common.endDate')}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>{renderAllergies()}</TableBody>
                </ProfessionalSummaryDataTable>
            )}

            <ProfessionalSummaryDataPlaceholder
                hasContent={patientHasAllergies()}
                isLoading={!areAllergiesLoaded}
                loadFailed={loadFailed}
                messageFailed={translateKey(
                    'professionalSummary.loadError',
                    translateKey(`professionalSummary.${ProfessionalSummaryDataType.allergies}`)
                )}
                messageNotFound={translateKey('professionalSummary.noAllergiesFound')}
            />
        </ProfessionalSummaryDataSection>
    );

    async function loadAllergies() {
        let initialAllergies;
        if (props.processActivityId) {
            try {
                initialAllergies = await loadProcessActivityAllergies(patient, props.processActivityId);
            } catch (error) {
                setLoadFailed(true);
            }
        } else {
            try {
                initialAllergies = await loadRequestAllergies(patient, props.requestId);
            } catch (error) {
                setLoadFailed(true);
            }
        }

        setAllergies(initialAllergies);
        setLoadFailed(false);
        setAreAllergiesLoaded(true);
    }

    function renderAllergies() {
        if (allergies && allergies.size > 0) {
            const allergiesRows = [];
            for (const [key, allergy] of allergies.entrySeq().toArray() as [string, AllergyRecord][]) {
                allergiesRows.push(
                    <TableRow key={allergy.id}>
                        <TableData style={{ width: '36px', minWidth: '36px' }}>
                            <Checkbox
                                checked={allergy.action === ProfessionalSummaryDataAction.pick}
                                onChange={async (event) => {
                                    setAllergies(
                                        allergies.setIn(
                                            [key, 'action'],
                                            event.target.checked
                                                ? ProfessionalSummaryDataAction.pick
                                                : ProfessionalSummaryDataAction.skip
                                        )
                                    );
                                }}
                            />
                        </TableData>
                        <TableData>{allergy.displayName}</TableData>
                        <TableData>{allergy.comment}</TableData>
                        <TableData
                            align="right"
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            <NullableFormattedDate value={allergy.startDate} />
                        </TableData>
                        <TableData
                            align="right"
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            <NullableFormattedDate value={allergy.endDate} />
                        </TableData>
                    </TableRow>
                );
            }

            return allergiesRows;
        }
    }

    function selectActiveAllergies() {
        if (allergies) {
            const updatedAllergies = allergies.map((allergy) => {
                if (allergy) {
                    return isCurrentAllergy(allergy)
                        ? allergy.set('action', ProfessionalSummaryDataAction.pick)
                        : allergy.set('action', ProfessionalSummaryDataAction.skip);
                }
            });
            setAllergies(updatedAllergies as Allergies);
        }
    }
}
